import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from '../../../base.service';

@Injectable({ providedIn: 'root' })
export class AdminSettingsService {
  constructor(private http: HttpClient, private base: BaseService) {}

  getRoles() {
    return this.http.get<any>(this.base.BASEURL + 'usersRoles');
  }

  getUsers() {
    return this.http.get<any>(this.base.BASEURL + 'users');
  }

  getRoleUsers(roleId: number) {
    return this.http.get<any>(this.base.BASEURL + 'usersRoles/' + roleId + '/users');
  }

  saveRoleUsers(roleId: number, userIds: number[]) {
    return this.http.put<any>(this.base.BASEURL + 'usersRoles/' + roleId + '/users', { userIds });
  }

  createRole(name: string) {
    return this.http.post<any>(this.base.BASEURL + 'usersRoles/', { name });
  }

  updateRole(id: number, name: string) {
    return this.http.put<any>(this.base.BASEURL + 'usersRoles/' + id, { name });
  }

  deleteRole(id: number) {
    return this.http.delete<any>(this.base.BASEURL + 'usersRoles/' + id);
  }

  getPermissionCatalog() {
    return this.http.get<any>(this.base.BASEURL + 'permissions/catalog');
  }

  getRolePermissions(roleId: number) {
    return this.http.get<any>(this.base.BASEURL + 'roles/' + roleId + '/permissions');
  }

  getMyPermissions() {
    return this.http.get<any>(this.base.BASEURL + 'permissions/me');
  }

  getMyRolePermissions(roleId: number) {
    return this.http.get<any>(this.base.BASEURL + 'permissions/me/' + roleId);
  }

  saveRolePermissions(roleId: number, permissionKeys: string[]) {
    return this.http.put<any>(this.base.BASEURL + 'roles/' + roleId + '/permissions', { permissionKeys });
  }

  getOrderPagePermissionCatalog() {
    return this.http.get<any>(this.base.BASEURL + 'order-page-permissions/catalog');
  }

  getRoleOrderPagePermissions(roleId: number) {
    return this.http.get<any>(this.base.BASEURL + 'roles/' + roleId + '/order-page-permissions');
  }

  getMyOrderPagePermissions(roleId?: number) {
    const suffix = roleId ? '/' + roleId : '';
    return this.http.get<any>(this.base.BASEURL + 'order-page-permissions/me' + suffix);
  }

  saveRoleOrderPagePermissions(roleId: number, settings: Record<string, boolean>) {
    return this.http.put<any>(this.base.BASEURL + 'roles/' + roleId + '/order-page-permissions', { settings });
  }

  getCommissionPolicies() {
    return this.http.get<any>(this.base.BASEURL + 'commission-policies');
  }

  createCommissionPolicy(payload: any) {
    return this.http.post<any>(this.base.BASEURL + 'commission-policies', payload);
  }

  updateCommissionPolicy(id: number, payload: any) {
    return this.http.put<any>(this.base.BASEURL + 'commission-policies/' + id, payload);
  }

  deleteCommissionPolicy(id: number) {
    return this.http.delete<any>(this.base.BASEURL + 'commission-policies/' + id);
  }

  getCommissionPolicyUsers(policyId: number) {
    return this.http.get<any>(this.base.BASEURL + 'commission-policies/' + policyId + '/users');
  }

  getRoleCommissionPolicy(roleId: number) {
    return this.http.get<any>(this.base.BASEURL + 'roles/' + roleId + '/commission-policy');
  }

  saveRoleCommissionPolicy(roleId: number, policyId: number | null) {
    return this.http.put<any>(this.base.BASEURL + 'roles/' + roleId + '/commission-policy', { policy_id: policyId });
  }

  getUserCommissionOverride(userId: number) {
    return this.http.get<any>(this.base.BASEURL + 'commission-policies/users/' + userId + '/override');
  }

  saveUserCommissionOverride(userId: number, payload: any) {
    return this.http.put<any>(this.base.BASEURL + 'commission-policies/users/' + userId + '/override', payload);
  }

  deleteUserCommissionOverride(userId: number) {
    return this.http.delete<any>(this.base.BASEURL + 'commission-policies/users/' + userId + '/override');
  }

  getSmtpSettings() {
    return this.http.get<any>(this.base.BASEURL + 'smtp-settings');
  }

  saveSmtpSettings(payload: any) {
    return this.http.put<any>(this.base.BASEURL + 'smtp-settings', payload);
  }

  testSmtp(to: string) {
    return this.http.post<any>(this.base.BASEURL + 'smtp-settings/test', { to });
  }

  getEmailNotificationTemplates() {
    return this.http.get<any>(this.base.BASEURL + 'email-notification-templates');
  }

  saveEmailNotificationTemplate(code: string, payload: any) {
    return this.http.put<any>(this.base.BASEURL + 'email-notification-templates/' + code, payload);
  }

  previewEmailNotificationTemplate(code: string, payload: any) {
    return this.http.post<any>(this.base.BASEURL + 'email-notification-templates/' + code + '/preview', payload);
  }

  getReportSchedules() {
    return this.http.get<any>(this.base.BASEURL + 'report-schedules');
  }

  saveReportSchedule(payload: any) {
    return this.http.post<any>(this.base.BASEURL + 'report-schedules', payload);
  }

  sendReportNow(payload: any) {
    return this.http.post<any>(this.base.BASEURL + 'report-schedules/send-now', payload);
  }

  deleteReportSchedule(id: number) {
    return this.http.delete<any>(this.base.BASEURL + 'report-schedules/' + id);
  }

  getAuditEvents(params: any = {}) {
    return this.http.get<any>(this.base.BASEURL + 'audit-events', { params });
  }

  getAuditArchiveSettings() {
    return this.http.get<any>(this.base.BASEURL + 'audit-archive-settings');
  }

  saveAuditArchiveSettings(payload: any) {
    return this.http.put<any>(this.base.BASEURL + 'audit-archive-settings', payload);
  }

  testAuditArchiveSettings() {
    return this.http.post<any>(this.base.BASEURL + 'audit-archive-settings/test', {});
  }

  archiveAuditEvents(days = 30) {
    return this.http.post<any>(this.base.BASEURL + 'audit-events/archive', { days });
  }
}
